import { useNavigation, useTranslate } from "@refinedev/core";
import { Collapse, Flex, Space, Typography } from "antd";
import { useAntTheme } from "hooks/useAntTheme/";
import styled from "styled-components";
import { BrandKit } from "../../types";
import { ColorPreview } from "./FlatBrandKitPreview";
import { FontPreviewText } from "./FontPreview";
import { useMemo, useState } from "react";
import { ListItemCard, StyledItemCard } from "pages/components/ListItemCard/";
import { radius } from "@libertify/ui";

export const BrandKitPreviewCard = ({
  brandKit,
  organizationId,
}: {
  brandKit: BrandKit;
  organizationId: string;
}) => {
  const { theme } = useAntTheme();
  const t = useTranslate();
  const { edit } = useNavigation();

  const fonts = useMemo(
    () => brandKit.assets.filter((asset) => asset.type === "Font"),
    [brandKit.assets]
  );

  const uploadedItems = useMemo(
    () => brandKit.assets.filter((asset) => asset.type !== "Font"),
    [brandKit.assets]
  );

  const [isCollapseOpen, setIsCollapseOpen] = useState(false);

  return (
    <ListItemCard
      title={brandKit.name}
      onEditClick={() => edit("media_brandkits", brandKit.id)}
      recordId={brandKit.id}
      resource={`media/${organizationId}/brand_kits`}
      invalidationResource={`media/${organizationId}/brand_kits?include_generated=false`}
    >
      <Flex gap={10}>
        {/* Colors */}
        <StyledSectionCard vertical>
          <StyledSectionTitle>
            {t(`media.brandkit.preview.colors`)}
          </StyledSectionTitle>
          <Flex align="center" style={{ flex: 1 }}>
            <Space>
              {[
                brandKit.palette.primary_color,
                brandKit.palette.secondary_color,
              ]
                .filter((color): color is string => !!color)
                .map((color) => (
                  <ColorPreview
                    key={color}
                    color={color}
                    borderColor={theme.colorTextBase}
                    size={20}
                  />
                ))}
            </Space>
          </Flex>
        </StyledSectionCard>
        {/* Fonts */}
        <StyledSectionCard vertical style={{ minHeight: 135 }}>
          <StyledSectionTitle>
            {t(`media.brandkit.preview.fonts`)}
          </StyledSectionTitle>
          <Flex vertical style={{ flex: 1, padding: 10 }}>
            {fonts.map((font) => (
              <FontPreviewText
                font={font.path}
                text={t(`media.brandkit.preview.${font.purpose}`)}
                key={`${font.asset_id}-${font.purpose}`}
                style={{ fontSize: 14, textAlign: "left", padding: 0 }}
              />
            ))}
          </Flex>
        </StyledSectionCard>
      </Flex>
      {/* Uploaded items */}
      <StyledSectionCard
        vertical
        gap={5}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        style={{ minHeight: 70 }}
      >
        {uploadedItems.length > 0 ? (
          <div
            onBlur={(e) => {
              if (!e.currentTarget.contains(e.relatedTarget)) {
                setIsCollapseOpen(false);
              }
            }}
          >
            <Collapse
              ghost
              activeKey={isCollapseOpen ? "1" : undefined}
              onChange={(keys) => setIsCollapseOpen(keys.length > 0)}
              items={[
                {
                  key: "1",
                  label: (
                    <StyledSectionTitle>
                      {t(`media.brandkit.preview.uploadedItems`, {
                        count: uploadedItems.length,
                      })}
                    </StyledSectionTitle>
                  ),
                  children: (
                    <Flex vertical gap={5}>
                      {uploadedItems.slice(0, 3).map((item) => {
                        const itemType = t(
                          `media.brandkit.preview.${item.type}`
                        );
                        let itemName = t(
                          `brandkit.components.BrandKitAssets.${item.purpose}`
                        );
                        if (item.type === "Picture" || item.type === "Video") {
                          itemName += ` ${itemType}`;
                        }
                        return (
                          <StyledItemCard
                            key={item.asset_id}
                            style={{
                              padding: 5,
                            }}
                          >
                            <Typography.Text
                              style={{
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {itemName}
                            </Typography.Text>
                          </StyledItemCard>
                        );
                      })}
                    </Flex>
                  ),
                },
              ]}
            />
          </div>
        ) : (
          <Typography.Text style={{ margin: "12px 16px" }}>
            {t(`media.brandkit.preview.noUploadedItems`)}
          </Typography.Text>
        )}
      </StyledSectionCard>
    </ListItemCard>
  );
};

const StyledSectionTitle = styled(Typography.Text)`
  font-weight: 500;
  font-size: 16px;
  letter-spacing: -1px;
  color: ${(props) => props.theme.colorTextDescription};
`;

const StyledSectionCard = styled(Flex)`
  border: 1px solid ${(props) => props.theme.colorBorder};
  padding: 10px;
  border-radius: ${radius.box}px;
  background-color: ${(props) => props.theme.colorBgElevated};
  flex: 1;

  .ant-collapse-expand-icon {
    color: ${(props) => props.theme.colorTextDescription};
  }
`;
